<script setup lang="ts">
import { computed } from 'vue'

import { Button, Tooltip, useConfirmModal } from '@collector/shared-ui'
import type { ParticipantSubType } from '@collector/sportsapi-client-legacy'

import type { PositionConfig } from '../../interfaces/LineupConfig'
import type { LineupModel } from '../../interfaces/LineupModel'
import ConfirmNoShirtNumberModal from './ConfirmNoShirtNumberModal.vue'

type Props = {
  name: PropertyKey
  type: 'new' | 'found' | 'squad'
  config: PositionConfig
  lineup: LineupModel
  shirtNr?: string | null
  subType?: ParticipantSubType | null
  disabled?: boolean
  disabledReasonText?: string
}
const props = defineProps<Props>()

type Emits = {
  add: [void]
}
const emit = defineEmits<Emits>()

// composables
const { openModalCancelConfirm } = useConfirmModal()

// computed
const addPlayerConfig = computed(() => {
  switch (props.type) {
    case 'new':
      return props.config.addNewPlayer
    case 'found':
      return props.config.addFoundPlayer
    case 'squad':
    default:
      return props.config.addSquadPlayer
  }
})
const subTypeDisabled = computed(
  () =>
    !!props.subType &&
    addPlayerConfig.value?.disableForSubTypes?.includes(props.subType),
)

const invalidShirtNr = computed(
  () => addPlayerConfig.value?.requireShirtNr && !props.shirtNr,
)
const countExceeded = computed(
  () =>
    props.config.maxCount !== undefined &&
    props.config.maxCount <= (props.lineup[props.name]?.length ?? 0),
)
const buttonDisabled = computed(
  () =>
    props.disabled ||
    invalidShirtNr.value ||
    countExceeded.value ||
    subTypeDisabled.value,
)

// methods
async function handleAdd(): Promise<void> {
  try {
    if (addPlayerConfig.value?.confirmMissingShirtNr && !props.shirtNr) {
      if (await openModalCancelConfirm(ConfirmNoShirtNumberModal)) emit('add')
    } else emit('add')
  } catch (_err) {
    //
  }
}
</script>

<template>
  <Tooltip lazy>
    <Button
      class="bg-neutral-light-8 border-neutral-light-0 min-w-11 font-bold"
      :class="[
        { 'cursor-pointer': !buttonDisabled },
        buttonDisabled || config.buttonClass,
      ]"
      :disabled="buttonDisabled"
      width="slim"
      size="sm"
      @click="handleAdd"
    >
      {{ config.buttonLabel }}
    </Button>

    <template
      v-if="
        ((invalidShirtNr || countExceeded || subTypeDisabled) && !disabled) ||
        disabledReasonText
      "
      #tooltip
    >
      <div class="grid">
        <div v-if="invalidShirtNr && !disabled">Shirt number is required</div>
        <div v-if="countExceeded && !disabled">
          {{ config.maxCountReasonText }}
        </div>
        <div v-if="disabledReasonText">
          {{ disabledReasonText }}
        </div>
        <div v-if="subTypeDisabled">
          Player with subtype "{{ subType }}" cannot be added to this position
        </div>
      </div>
    </template>
  </Tooltip>
</template>
